import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ReactNode } from "react";

/** A titled group of superpowers. */
export function AdminPanelSection({
  children,
  description,
  title,
}: {
  children: ReactNode;
  description?: string;
  title: string;
}) {
  return (
    <View className="gap-2">
      <View className="gap-0.5 px-1">
        <Text accessibilityRole="header" className="text-sm font-semibold text-foreground">
          {title}
        </Text>
        {description === undefined ? null : (
          <Text className="text-xs text-muted-foreground">{description}</Text>
        )}
      </View>
      <View className="gap-3 overflow-hidden rounded-xs bg-surface p-3">{children}</View>
    </View>
  );
}

export function AdminPanelButton({
  accessibilityLabel,
  disabled = false,
  onPress,
  title,
  variant = "secondary",
}: {
  accessibilityLabel?: string;
  disabled?: boolean;
  onPress: () => void;
  title: string;
  variant?: "primary" | "secondary";
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={cn(
        "items-center justify-center rounded-full px-3.5 py-1.5 active:opacity-70 disabled:opacity-40",
        variant === "primary" ? "bg-primary" : "bg-muted",
      )}
      disabled={disabled}
      onPress={onPress}
    >
      <Text
        className={cn(
          "text-sm font-medium",
          variant === "primary" ? "text-primary-foreground" : "text-foreground",
        )}
      >
        {title}
      </Text>
    </Pressable>
  );
}

/** The latest outcome of a superpower, announced to screen readers as it changes. */
export function AdminPanelStatus({ isError = false, text }: { isError?: boolean; text: string }) {
  return (
    <Text
      accessibilityLiveRegion="polite"
      className={cn("text-xs", isError ? "text-destructive" : "text-muted-foreground")}
    >
      {text}
    </Text>
  );
}
